// ----------------------------------------------------------------------------
// 📊 PRODUCTION-SAFE LOGGER
// Conditional logging that respects environment settings
// ----------------------------------------------------------------------------

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

import { trackException } from './telemetry';

class Logger {
  private isProduction: boolean;

  constructor() {
    this.isProduction = import.meta.env.MODE === 'production' || 
                       import.meta.env.VITE_ENVIRONMENT === 'production';
  }

  private shouldLog(level: LogLevel): boolean {
    // Never log in production except errors
    if (this.isProduction) {
      return level === 'error';
    }
    return true;
  }

  log(...args: any[]): void {
    if (this.shouldLog('log')) {
      console.log(...args);
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(...args);
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...args);
    }
  }

  error(...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(...args);
      // Surface in App Insights (no-op when telemetry is not configured).
      const err = args.find((a) => a instanceof Error) ?? args.map(String).join(' ');
      trackException(err, { source: 'logger' });
    }
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.debug(...args);
    }
  }
}

export const logger = new Logger();
